import { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from '@/src/shadComponents/ui/card';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/src/shadComponents/ui/select';
import { Badge } from '@/src/shadComponents/ui/badge';
import {
    AlertCircle,
    Download,
    Plus,
    Trash2,
    Undo,
    Redo,
    Save,
    RefreshCcw,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/src/shadComponents/ui/alert';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import useWhitelabelSettings from '@/src/WhitelabelSettings/components/hooks/useWhitelabelSettings';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useGetGalleries from '@/src/GalleryConfiguration/components/hooks/useGetGalleries';
import { toast } from 'sonner';

interface ActionObject {
    action: string;
    data: Record<string, string>;
}

interface ActionDefinition {
    id?: number;
    activationId: number;
    actions: ActionObject[];
    queryParams?: string[];
}

interface ValidationError {
    message: string;
    actionIndex?: number;
    field?: string;
}

interface ActionCodeBuilderProps {
    activationId: number;
}

const SUPPORTED_ACTIONS = {
    navigate: {
        name: 'Navigate',
        description: 'Navigate to a destination',
        fields: ['destination'],
    },
    reset: {
        name: 'Reset',
        description: 'Reset the application state',
        fields: [],
    },
    setValue: {
        name: 'Set Value',
        description: 'Set a value for a specific key',
        fields: ['valueKey', 'value'],
    },
    share: {
        name: 'Share',
        description: 'Share content to a destination',
        fields: ['destination', 'shareType', 'disclaimerAccepted'],
    },
    print: {
        name: 'Print',
        description: 'Print a value',
        fields: ['value'],
    },
    searchShares: {
        name: 'Search Shares',
        description: 'Search for shares and store results',
        fields: ['destination', 'resultKey'],
    },
    setNextProcessingFlow: {
        name: 'Set Processing Flow',
        description: 'Set the processing flow for the session',
        fields: ['flowId'],
    },
    capture: {
        name: 'Capture',
        description: 'Capture media',
        fields: [],
    },
};

export default function ActionCodeBuilder({
    activationId,
}: ActionCodeBuilderProps) {
    const [actionDefinition, setActionDefinition] =
        useState<ActionDefinition | null>(null);
    const [actions, setActions] = useState<ActionObject[]>([]);
    const [queryParams, setQueryParams] = useState<string[]>([]);
    const [gallery, setGallery] = useState<any>(null);
    const [testValues, setTestValues] = useState<Record<string, string>>({});
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
        [],
    );
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [isGeneratingQR, setIsGeneratingQR] = useState(false);
    const [history, setHistory] = useState<ActionObject[][]>([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [galleryUrl, setGalleryUrl] = useState<string | null>(null);
    const { getOrgDomain } = useWhitelabelSettings();
    const { org } = useUserOrg();
    const { getGalleriesForActivation } = useGetGalleries();

    const authenticatedApi = useAuthenticatedApi();

    // Load existing action definition
    useEffect(() => {
        loadActionDefinition();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    // Track changes for undo/redo
    useEffect(() => {
        console.log('actions', actions);
        console.log('actionDefinition', actionDefinition);
        if (
            JSON.stringify(actions) !==
            JSON.stringify(actionDefinition?.actions)
        ) {
            setHasUnsavedChanges(true);
        } else {
            setHasUnsavedChanges(false);
        }
    }, [actions, actionDefinition]);

    useEffect(() => {
        if (actions.length > 0 && Object.keys(testValues).length > 0) {
            generateQRCode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testValues, actions]);

    // Extract query parameters whenever actions change
    useEffect(() => {
        extractQueryParams();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actions]);

    const loadActionDefinition = async () => {
        setIsLoading(true);
        try {
            const response = await authenticatedApi.get(
                `/activations/${activationId}/action-definition`,
            );
            const galleries = await getGalleriesForActivation(activationId);
            const gallery = galleries[0];
            const galleryUrl = await getOrgDomain(org?.id);
            const data = response.data;
            setActionDefinition(data);
            setActions(data.actions || []);
            setQueryParams(data.queryParams || []);
            setGalleryUrl(galleryUrl);
            setGallery(gallery);

            // Initialize history
            const initialHistory = [data.actions || []];
            setHistory(initialHistory);
            setHistoryIndex(0);
        } catch (error: any) {
            if (error.response?.status === 404) {
                // No action definition exists yet
                setActionDefinition(null);
                setActions([]);
                setQueryParams([]);
                setHistory([[]]);
                setHistoryIndex(0);
            } else {
                console.error('Error loading action definition:', error);
            }
        }
        setIsLoading(false);
    };

    const extractQueryParams = () => {
        const params = new Set<string>();
        const variableRegex = /\$\{([^}]+)\}/g;

        actions.forEach((action) => {
            Object.values(action.data).forEach((value) => {
                let match;
                while ((match = variableRegex.exec(value)) !== null) {
                    params.add(match[1]);
                }
            });
        });

        const sortedParams = Array.from(params).sort();
        setQueryParams(sortedParams);

        // Initialize test values for new params
        const newTestValues: Record<string, string> = {};
        sortedParams.forEach((param) => {
            if (!(param in newTestValues)) {
                newTestValues[param] = '';
            }
        });
        setTestValues(newTestValues);
    };

    const addToHistory = (newActions: ActionObject[]) => {
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push([...newActions]);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    };

    const undo = () => {
        if (historyIndex > 0) {
            const previousActions = history[historyIndex - 1];
            setActions([...previousActions]);
            setHistoryIndex(historyIndex - 1);
        }
    };

    const redo = () => {
        if (historyIndex < history.length - 1) {
            const nextActions = history[historyIndex + 1];
            setActions([...nextActions]);
            setHistoryIndex(historyIndex + 1);
        }
    };

    const validateActions = (): ValidationError[] => {
        const errors: ValidationError[] = [];

        if (actions.length === 0) {
            errors.push({ message: 'At least one action is required' });
            return errors;
        }

        actions.forEach((action, index) => {
            if (!action.action) {
                errors.push({
                    message: 'Action type is required',
                    actionIndex: index,
                });
                return;
            }

            if (!(action.action in SUPPORTED_ACTIONS)) {
                errors.push({
                    message: `Unsupported action type: ${action.action}`,
                    actionIndex: index,
                });
                return;
            }

            const requiredFields =
                SUPPORTED_ACTIONS[
                    action.action as keyof typeof SUPPORTED_ACTIONS
                ].fields;
            requiredFields.forEach((field) => {
                if (!action.data[field]) {
                    errors.push({
                        message: `Field '${field}' is required for action '${action.action}'`,
                        actionIndex: index,
                        field,
                    });
                }
            });
        });

        return errors;
    };

    const addAction = () => {
        const newAction: ActionObject = {
            action: 'navigate',
            data: { destination: '' },
        };
        const newActions = [...actions, newAction];
        addToHistory(newActions);
        setActions(newActions);
    };

    const cleanGalleryUrl = galleryUrl
        ? galleryUrl.endsWith('/')
            ? galleryUrl.slice(0, -1)
            : galleryUrl
        : null;

    const updateAction = (
        index: number,
        field: 'action' | string,
        value: string,
    ) => {
        const newActions = [...actions];

        if (field === 'action') {
            // Reset data when action type changes
            const actionType = value as keyof typeof SUPPORTED_ACTIONS;
            const requiredFields = SUPPORTED_ACTIONS[actionType].fields;
            newActions[index] = {
                action: value,
                // @ts-ignore
                data: requiredFields.reduce((acc, field) => {
                    acc[field] = '';
                    return acc;
                }, {} as Record<string, string>),
            };
        } else {
            newActions[index] = {
                ...newActions[index],
                data: {
                    ...newActions[index].data,
                    [field]: value,
                },
            };
        }

        addToHistory(newActions);
        setActions(newActions);
    };

    const removeAction = (index: number) => {
        const newActions = actions.filter((_, i) => i !== index);
        addToHistory(newActions);
        setActions(newActions);
    };

    const saveActionDefinition = async () => {
        const errors = validateActions();
        setValidationErrors(errors);

        if (errors.length > 0) {
            return;
        }

        setIsSaving(true);
        try {
            if (actionDefinition) {
                // Update existing
                await authenticatedApi.put(
                    `/activations/${activationId}/action-definition`,
                    {
                        actions,
                    },
                );
            } else {
                // Create new
                await authenticatedApi.post(
                    `/activations/${activationId}/action-definition`,
                    {
                        actions,
                    },
                );
            }

            // Reload to get updated data
            await loadActionDefinition();
            setHasUnsavedChanges(false);
        } catch (error) {
            console.error('Error saving action definition:', error);
        }
        setIsSaving(false);
    };

    const generateQRCode = async () => {
        setIsGeneratingQR(true);
        try {
            await authenticatedApi.put(
                `/activations/${activationId}/action-definition`,
                {
                    actions,
                },
            );

            const response = await authenticatedApi.post(
                `/activations/${activationId}/action-definition/qr-code`,
                { queryParams: testValues },
            );
            setQrCodeUrl(response.data.qrCodeDataUrl);
        } catch (error) {
            console.error('Error generating QR code:', error);
        }
        setIsGeneratingQR(false);
    };

    const downloadQRCode = async () => {
        try {
            const queryString = new URLSearchParams(testValues).toString();
            const response = await authenticatedApi.get(
                `/activations/${activationId}/action-definition/qr-code/download?${queryString}`,
                { responseType: 'blob' },
            );

            const blob = new Blob([response.data], { type: 'image/png' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `activation-${activationId}-qr-code.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading QR code:', error);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-lg">Loading action definition...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-4">
            {/* Header with actions */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold">
                        Clickerbooth Code Builder
                    </h2>
                    <p className="text-gray-600">
                        Define actions that will be encoded into a Clickerbooth
                        Code to create automations with custom data.
                    </p>
                    <p className="text-gray-600">
                        To use this code, scan the QR code or visit{' '}
                        <code
                            className="bg-gray-100 text-xs px-2 py-1 rounded cursor-pointer"
                            onClick={(e) => {
                                navigator.clipboard.writeText(
                                    `${cleanGalleryUrl}/qr-code/gallery/${
                                        gallery?.id
                                    }?${new URLSearchParams(
                                        testValues,
                                    ).toString()}`,
                                );
                                toast.success('Copied to clipboard');
                            }}
                        >
                            {`${cleanGalleryUrl}/qr-code/gallery/${
                                gallery?.id
                            }?${new URLSearchParams(testValues).toString()}`}
                        </code>
                    </p>
                </div>
                <div className="flex items-center space-x-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={undo}
                        disabled={historyIndex <= 0}
                    >
                        <Undo className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={redo}
                        disabled={historyIndex >= history.length - 1}
                    >
                        <Redo className="h-4 w-4" />
                    </Button>
                    <Button
                        onClick={saveActionDefinition}
                        disabled={isSaving || !hasUnsavedChanges}
                    >
                        <Save className="h-4 w-4 mr-2" />
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>

            {/* Validation errors */}
            {validationErrors.length > 0 && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        <div className="space-y-1">
                            {validationErrors.map((error, index) => (
                                <div key={index}>
                                    {error.actionIndex !== undefined
                                        ? `Action ${error.actionIndex + 1}: ${
                                              error.message
                                          }`
                                        : error.message}
                                </div>
                            ))}
                        </div>
                    </AlertDescription>
                </Alert>
            )}

            {/* Actions builder */}
            <Card>
                <CardHeader>
                    <CardTitle>Actions</CardTitle>
                    <CardDescription>
                        Define the sequence of actions to be executed when the
                        QR code is scanned
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {actions.map((action, index) => (
                        <Card key={index} className="p-4">
                            <div className="flex items-start justify-between mb-4">
                                <h4 className="font-semibold">
                                    Action {index + 1}
                                </h4>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => removeAction(index)}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <label className="text-sm font-medium">
                                        Action Type
                                    </label>
                                    <Select
                                        value={action.action}
                                        onValueChange={(value) =>
                                            updateAction(index, 'action', value)
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(
                                                SUPPORTED_ACTIONS,
                                            ).map(([key, info]) => (
                                                <SelectItem
                                                    key={key}
                                                    value={key}
                                                >
                                                    {info.name} -{' '}
                                                    {info.description}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {SUPPORTED_ACTIONS[
                                    action.action as keyof typeof SUPPORTED_ACTIONS
                                ]?.fields.map((field) => (
                                    <div key={field}>
                                        <label className="text-sm font-medium">
                                            {field}
                                        </label>
                                        <Input
                                            value={action.data[field] || ''}
                                            onChange={(e) =>
                                                updateAction(
                                                    index,
                                                    field,
                                                    e.target.value,
                                                )
                                            }
                                            placeholder={`Enter ${field} (use \${paramName} for variables)`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </Card>
                    ))}

                    <Button onClick={addAction} variant="outline" className="">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Action
                    </Button>
                </CardContent>
            </Card>
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">Testing</h2>
                <div className="flex-1 flex items-center justify-end" />
                <Button onClick={generateQRCode} disabled={isGeneratingQR}>
                    <RefreshCcw className="h-4 w-4" />
                </Button>
                {qrCodeUrl && (
                    <Button variant="outline" onClick={downloadQRCode}>
                        <Download className="h-4 w-4 mr-2" />
                    </Button>
                )}
            </div>
            <div className="flex items-start flex-row justify-between align-top">
                {/* Query parameters */}
                <Card>
                    <CardHeader>
                        <CardTitle>Query Parameters</CardTitle>
                        <CardDescription>
                            Variables found in your actions. Set test values for
                            QR code generation.
                        </CardDescription>
                    </CardHeader>
                    {queryParams.length > 0 && (
                        <CardContent className="space-y-4">
                            <div className="flex flex-wrap gap-2 mb-4">
                                {queryParams.map((param) => (
                                    <Badge key={param} variant="secondary">
                                        {param}
                                    </Badge>
                                ))}
                            </div>

                            {queryParams.map((param) => (
                                <div key={param}>
                                    <label className="text-sm font-medium">
                                        {param}
                                    </label>
                                    <Input
                                        value={testValues[param] || ''}
                                        onChange={(e) =>
                                            setTestValues({
                                                ...testValues,
                                                [param]: e.target.value,
                                            })
                                        }
                                        placeholder={`Test value for ${param}`}
                                    />
                                </div>
                            ))}
                        </CardContent>
                    )}
                </Card>
                {/* QR Code generation */}
                {actionDefinition && actions.length > 0 && (
                    <CardContent className="space-y-4">
                        <div className="flex space-x-2 flex-col">
                            {qrCodeUrl && (
                                <div className="flex justify-center">
                                    <img
                                        src={qrCodeUrl}
                                        alt="Generated QR Code"
                                        className="border rounded-lg max-w-md"
                                    />
                                </div>
                            )}
                        </div>
                    </CardContent>
                )}
            </div>
        </div>
    );
}
